import { useDisclosure, useToast, extendTheme, ChakraProvider, Center, Box, Fade, Image } from "@chakra-ui/react";
import { PropsWithChildren, useState, useMemo, useCallback, useEffect, useContext, createContext } from "react";
import { Helmet } from "react-helmet";
import { ModalLoginAndPlan } from "../components/molecules/VirtualModal";
import { LoadingScreen } from "../components/organisms/LoadingScreen";
import { axiosInstance, setAccessToken, axiosInstanceV2, isAuthenticated } from "../service/axios";
import { getBenefits, getLandingProd } from "../service/club/api";
import { useProfile } from "./ProfileProvider";
import { parseAgreementToBookstore } from "../service/club/utils";
import { spawnAppEvent } from "../service/utils";
import { IThemeContext, ILoadingContext } from "../interfaces/types";
import pako from 'pako';
import { IUser } from "../service/types";

const defaultThemeContextData: IThemeContext = {
  slug: '',
  exClientModule: false,
  hasIntegratedPurchaseOnMobileStore: false,
  childrenList: [],
  id: null,
  isNative: false,
  integratedLogin: undefined,
  integrationId: undefined,
  name: '',
  logo: '',
  idp: '',
  email: 'suporte@partiu.com.br',
  telephone: '(21) 97420-4416',
  colors: {
    primaryColor: '#747474',
    primaryFontColor: '#747474',
    secondaryColor: '#747474',
    secondaryFontColor: '#747474',
    menuColor: '#747474',
    menuFontColor: '#747474',
    graphColors: [],
  },
  banners: [],
  recurringBook: true,
  isApp: false,
  openModalLogin: () => { },
  localModule: false,
  isIOS: false,
  isSetup: false,
  privacyPolicy: '',
  legalText: '',
  metadata: {},
  getValidSubscriptions: () => [],
  // Permissions
  allowCpfLoginMethod: false,
  allowPromocodeLoginMethod: false,
  // Menu variables for dashboards
  isMenuOpen: false,
  cpfModule: false,
  // Update functions
  setMenuOpen: () => { },
  updateTheme: () => { },
  resetTheme: () => { },
  setLoading: () => { },
  fetchBenefits: () => { },
  benefits: [],
  children: [],
  bookstoreLabels: null,
};

const ThemeContext = createContext(defaultThemeContextData);

export function ThemeProvider({
  children,
  themeResolver,
}: { themeResolver: () => Promise<IThemeContext> } & PropsWithChildren) {
  const modalLogin = useDisclosure();
  const { logout, profile, updateProfile } = useProfile();
  const [benefits, setBenefits] = useState<any>([]);
  const [setupData, setSetupData] = useState({
    isSetup: false,
    animate: false,
    exit: false,
  });
  const [notFound, setNotFound] = useState(false);
  const [isLogged, setIsLogged] = useState(false);
  const [isNative, setIsNative] = useState(true);
  const [fetchBenefitsFlag, setFetchBenefitsFlag] = useState(false);
  const [urlData, setUrlData] = useState({ isApp: false, isIOS: false });
  const [data, setData] = useState<IThemeContext>(defaultThemeContextData);
  const [loadingData, setLoadingData] = useState<ILoadingContext>({
    isLoading: false,
    loadingDelay: 0,
    loadingDescription: 'Carregando...',
  });

  const toast = useToast();

  const theme = useMemo(
    () =>
      extendTheme({
        fonts: {
          heading: `'DM Sans', sans-serif;`,
          body: `'DM Sans', sans-serif;`,
        },
        colors: {
          ...data.colors,
        },
      }),
    [data]
  );

  const updateTheme = useCallback(
    (newThemeData: IThemeContext) => {
      setData({ ...data, ...newThemeData });
    },
    [data, setData]
  );

  function checkIfNativeModule(url: string) {
    const regex = /^(https?:\/\/)?([\w.-]*\.)?(partiu\.com\.br|ebooks\.app\.br)(\/|$)/i;
    const match = url.match(regex);

    if (match) {
      const dominio = match[3];
      const temPalavraChave = url.includes('biblioteca') || url.includes('revista');

      if (temPalavraChave || dominio === 'ebooks.app.br') {
        return 'livro';
      } else {
        return 'clube';
      }
    }

    return null;
  }

  function getDomain(url: string) {
    try {
      const urlObj = new URL(url);
      const partesDominio = urlObj.hostname.split('.');

      return partesDominio.length > 2 ? partesDominio[0] : null;
    } catch (error) {
      return null;
    }
  }

  const fetchBenefits = async (agreementId: number) => {
    const benefitsArray = await getBenefits({
      agreement_id: agreementId,
    });

    const blobUrls = await Promise.all(

      benefitsArray.map(async (category: any) => {
        category.modules
          .filter((mod: any) => mod.active)
          .map(async (module: any) => {
            module.is_gif = module.icon?.includes('.gif');

            try {
              const response = await fetch(module.icon);
              const blob = await response.blob();
              module.icon = URL.createObjectURL(blob);
            } catch (error) { }

            const verifica = checkIfNativeModule(module.url);

            const subdominio = getDomain(module.url);

            if (verifica === 'livro' && subdominio) {
              const response: any = await getLandingProd(subdominio, 'bookstore');

              module.data = parseAgreementToBookstore(response);

              delete module.data.childrenList;

            } else if (verifica === 'clube' && subdominio) {
              const response: any = await getLandingProd(subdominio, 'club');
              module.data = parseAgreementToBookstore(response);
              delete module.data.childrenList;
            }

            return module;
          });
        return category;
      })
    );
    setBenefits([...blobUrls]);
  };

  function getQueryParam(name: string) {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
  }

  const base64Data = getQueryParam('data');

  useEffect(() => {
    if (!profile) {
      setIsLogged(false);
    } else {
      setIsLogged(true);
    }
  }, [profile]);

  useEffect(() => {
    if (!fetchBenefitsFlag && isLogged && data?.id) {
      setFetchBenefitsFlag(true);
      fetchBenefits(data?.id);
    } else if (!isLogged && data?.id) {
      fetchBenefits(data?.id);
    }
  }, [fetchBenefitsFlag, isLogged, data]);

  useEffect(() => {
    const params = Object.fromEntries(new URLSearchParams(location.search));
    const urlData = {
      isApp: params.isApp === 'true',
      isIOS: params.isIOS === 'true',
    };
    const existingUrlData = JSON.parse(localStorage.getItem('urlData') || 'null');

    if (existingUrlData) {
      if (!params.isApp) urlData['isApp'] = existingUrlData['isApp'];
      if (!params.isIOS) urlData['isIOS'] = existingUrlData['isIOS'];
    }

    if (params.isNative) {
      setIsNative(true);
    } else {
      setIsNative(false);
    }

    localStorage.setItem('urlData', JSON.stringify(urlData));

    setUrlData(urlData);
  }, []);

  useEffect(() => {
    const params = Object.fromEntries(new URLSearchParams(location.search));
    const urlData = {
      isApp: params.isApp === 'true',
      isIOS: params.isIOS === 'true',
    };
    const existingUrlData = JSON.parse(localStorage.getItem('urlData') || 'null');

    if (existingUrlData) {
      if (!params.isApp) urlData['isApp'] = existingUrlData['isApp'];
      if (!params.isIOS) urlData['isIOS'] = existingUrlData['isIOS'];
    }

    localStorage.setItem('urlData', JSON.stringify(urlData));

    setUrlData(urlData);
  }, []);

  useEffect(() => {
    const token = getQueryParam('token');
    const tokenExpirationDate = getQueryParam('tokenExpirationDate');

    if (token) {
      setAccessToken(token, tokenExpirationDate);
      setIsLogged(true);
      updateProfile({});
    }
  }, []);

  useEffect(() => {
    if (base64Data) {
      setSetupData({ ...setupData, isSetup: true, animate: false, exit: true });
      const binaryData = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));

      const jsonData = pako.inflate(binaryData, { to: 'string' }) as string;
      const agreement = JSON.parse(jsonData);
      setData({ ...agreement });
      spawnAppEvent('splashDone');
      localStorage.setItem('theme', JSON.stringify(agreement));
      if (agreement.id) {
        axiosInstance.defaults.headers['x-agreement-id'] = agreement.id?.toString();
        axiosInstanceV2.defaults.headers['x-agreement-id'] = agreement.id?.toString();
      }
      if (agreement.token) {
        setAccessToken(agreement.token, agreement.tokenExpirationDate);
        setIsLogged(true);
      }
    } else {
      const legal = window.location.pathname.includes('/legal/');
      if (legal) {
        setSetupData({ ...setupData, isSetup: false, animate: false, exit: true });
      } else {
        setSetupData({ ...setupData, isSetup: false, animate: false, exit: false });
      }

      themeResolver().then((agreement: IThemeContext) => {
        if (!agreement) {
          setNotFound(true);
          return;
        }
        setData(agreement);
        spawnAppEvent('splashDone');
        localStorage.setItem('theme', JSON.stringify(agreement));
        if (agreement.id) localStorage.setItem('agreementId', agreement.id.toString());
        if (isAuthenticated()) {
          setIsLogged(true);
        }

        if (!legal) {
          setTimeout(() => setSetupData({ ...setupData, animate: true }), 1500);
        }
        setTimeout(() => setSetupData({ ...setupData, exit: true }), 2500);
      });
    }
  }, [themeResolver, base64Data]);

  return (
    <ThemeContext.Provider
      value={{
        ...data,
        ...urlData,
        benefits,
        isNative,
        fetchBenefits,
        openModalLogin: modalLogin.onOpen,
        setLoading: (value: boolean, description: string = '', delay: number = loadingData.loadingDelay) => {
          setLoadingData({ isLoading: value, loadingDescription: description, loadingDelay: delay });
        },
        setMenuOpen: (value: boolean) => setData({ ...data, isMenuOpen: value }),
        updateTheme,
        resetTheme: () => setData(defaultThemeContextData),
        getValidSubscriptions: (profile: IUser | undefined) => {
          if (!profile) return [];
          return profile.subscriptions
            .filter((sub) => sub.plan.agreement.id === data.id)
            .sort((a, b) => a.plan.level - b.plan.level);
        },
      }}
    >
      <ChakraProvider theme={theme}>
          <LoadingScreen
            description={loadingData.loadingDescription}
            isOpen={loadingData.isLoading}
            loadingDelay={loadingData.loadingDelay}
          />
          <Helmet>
            <title>{data.name}</title>
            <meta name="description" content={'Clube de Vantagens - Partiu'} />
          </Helmet>

          {notFound && (
            <Center minH="100vh" bg="white" px={4}>
              <Box textAlign="center" py={8} px={8} borderRadius="lg" boxShadow="lg" maxW="md" w="100%" bg="white">
                <Image src="/partiu-hub.png" alt="Logo Partiu Hub" maxW={32} mb={8} mx="auto" />
                <Box fontSize="6xl" fontWeight="bold" color="#ed813a" mb={2} letterSpacing="tight">
                  404
                </Box>
                <Box fontSize="2xl" color="#747474" fontWeight="semibold" mb={6}>
                  Ops! Página não encontrada ou convênio desativado.
                </Box>
                <a href="https://partiu.com.br/" style={{ textDecoration: 'none' }}>
                  <Box
                    as="span"
                    px={8}
                    py={3}
                    bg="#ed813a"
                    color="white"
                    borderRadius="md"
                    fontWeight="medium"
                    _hover={{ bg: '#ff944d', textDecoration: 'none' }}
                    transition="background 0.2s"
                    cursor="pointer"
                    display="inline-block"
                  >
                    Entre em contato com o suporte
                  </Box>
                </a>
              </Box>
            </Center>
          )}
          <Fade
            transition={{ exit: { duration: 0.5 }, enter: { duration: 1, delay: 0.5 } }}
            in={!!(data.logo && !setupData.exit)}
          >
            <Center
              alignContent={'center'}
              justifyContent={'center'}
              pos={'fixed'}
              left={'0px'}
              backgroundColor={'#F1F4F5'}
              top={'0px'}
              width="100%"
              height="100vh"
              pointerEvents={'none'}
              zIndex={1000}
            >
              <Box
                width={'50px'}
                height={'50px'}
                borderRadius={'100%'}
                pos={'absolute'}
                backgroundColor={data.colors.primaryColor}
                transition={'2s'}
                transform={setupData.animate ? 'scale(100)' : 'scale(0)'}
              />
              <Image maxWidth={'500px'} src={data.logo} w={'70%'} margin="auto" transition={'0.1s'} />
            </Center>
          </Fade>
          {setupData.exit && (
            <Fade in={setupData.exit} transition={{ enter: { duration: 0.5, delay: 0 } }}>
              {children}
            </Fade>
          )}

          <ModalLoginAndPlan isOpen={modalLogin.isOpen} onClose={modalLogin.onClose} />
      </ChakraProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
